import { useEffect, useRef } from 'react';
import { Product } from '../data/products';

interface PrefetchOptions {
  maxPrefetch?: number; // Maximum number of images to prefetch
  priority?: 'high' | 'low'; // Prefetch priority
}

/**
 * Custom hook to prefetch product images based on current quiz answers
 * This helps load likely result images in advance for faster display
 */
export const useImagePrefetch = (
  likelyProducts: Product[], 
  options: PrefetchOptions = {}
) => {
  const { maxPrefetch = 6, priority = 'low' } = options;
  const prefetchedUrls = useRef(new Set<string>());
  const prefetchElements = useRef<HTMLLinkElement[]>([]);

  useEffect(() => {
    // Clear previous prefetch links
    prefetchElements.current.forEach(link => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    });
    prefetchElements.current = [];

    // Get the top products to prefetch (avoid duplicates)
    const urlsToPrefetch = likelyProducts
      .slice(0, maxPrefetch)
      .map(product => product.imageUrl)
      .filter(url => !prefetchedUrls.current.has(url));

    // Create prefetch links for each image
    urlsToPrefetch.forEach(imageUrl => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = imageUrl;
      link.as = 'image';
      
      // Set fetch priority if supported
      if ('fetchPriority' in HTMLLinkElement.prototype) {
        (link as any).fetchPriority = priority;
      }

      // Add to document head
      document.head.appendChild(link);
      prefetchElements.current.push(link);
      prefetchedUrls.current.add(imageUrl);
    });

    // Cleanup function
    return () => {
      prefetchElements.current.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
      prefetchElements.current = [];
    };
  }, [likelyProducts, maxPrefetch, priority]);

  // Return prefetch status
  return {
    prefetchedCount: prefetchedUrls.current.size,
    isPrefetching: prefetchElements.current.length > 0
  };
};

/**
 * Utility function to preload images using Image() constructor
 * This is more aggressive than prefetch and loads images immediately
 */
export const preloadImages = (imageUrls: string[], onProgress?: (loaded: number, total: number) => void) => {
  let loadedCount = 0;
  const totalCount = imageUrls.length;

  return Promise.all(
    imageUrls.map(url => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          loadedCount++;
          onProgress?.(loadedCount, totalCount);
          resolve();
        };
        
        img.onerror = () => {
          loadedCount++;
          onProgress?.(loadedCount, totalCount);
          // Don't reject on error, just continue
          resolve();
        };
        
        img.src = url;
      });
    })
  );
};
